import React, { useState } from 'react';
import { Checkbox, Col, Input, Row, Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { flatten } from 'lodash';

import { StatusType } from '../../common/types';
import { useGetRecruitmentStatusDictionary } from '../../common/useGetRecruitmentStatusDictionary';
import { StyledButton } from '../../components/Button';
import Icon from '../../icons/Icon';

import { AddButtonIcon, SaveButtonIcon, StyledDeleteButton, StyledEditButton } from './DictionaryItemsList';
import { DictionaryItemsListContainer, InputListItem, ListLabel, StyledListItem } from './itemsList.styles';
import { useAddRecruitmentStatusDefinition } from './useAddRecruitmentStatusDefinition';
import { useDeleteRecruitmentStatusDefinition } from './useDeleteRecruitmentStatusDefinition';
import { useUpdateRecruitmentStatusDefinition } from './useUpdateRecruitmentStatusDefinition';

const reponsibilityOptions = [
  {
    value: 'COORDINATOR',
    label: 'COORDINATOR',
  },
  {
    value: 'BACKOFFICE',
    label: 'BACKOFFICE',
  },
  {
    value: 'RECRUITER',
    label: 'RECRUITER',
  },
  {
    value: 'INTERVIEWER',
    label: 'INTERVIEWER',
  },
];

const daysToReminderOptions: DefaultOptionType[] = [
  { value: null, label: 'DO NOT SEND' },
  ...Array.from({ length: 7 }, (_, index) => {
    const value = index + 1;
    return { value, label: `${value} day${value === 1 ? '' : 's'}` };
  }),
];

type RecruitmentStatusListItemProps = {
  item: StatusType;
  onSave: (item: StatusType) => void;
  onDelete: (id: number) => void;
};

const RecruitmentStatusListItem: React.FC<RecruitmentStatusListItemProps> = ({ item, onDelete }) => {
  const [edit, setEdit] = useState(false);

  const [name, setName] = useState(item.name);
  const [actionRequired, setActionRequired] = useState(item.actionRequired);
  const [countedAsCvSent, setCountedAsCvSent] = useState(item.countedAsCvSent);
  const [responsibility, setResponsibility] = useState(item.responsibility);
  const [daysToReminder, setDaysToReminder] = useState<number | null>(item.daysToReminder ?? null);

  const updateRecruitmentStatusDefinition = useUpdateRecruitmentStatusDefinition();

  const handleSaveItem = () => {
    updateRecruitmentStatusDefinition.mutate({
      queryParams: { id: item.id },
      data: { id: item.id, name, actionRequired, countedAsCvSent, responsibility, daysToReminder },
    });
    setEdit(false);
  };

  const handleCancelEditItem = () => {
    setName(item.name);
    setActionRequired(item.actionRequired);
    setCountedAsCvSent(item.countedAsCvSent);
    setResponsibility(item.responsibility);
    setDaysToReminder(item.daysToReminder ?? null);
    setEdit(false);
  };

  return (
    <>
      {edit ? (
        <InputListItem>
          <Row gutter={10} align="middle">
            <Col span={5}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Col>

            <Col span={5}>
              <Checkbox
                checked={!!actionRequired}
                onChange={() => setActionRequired(!actionRequired)}
                style={{ fontSize: '16px' }}
              >
                Action required
              </Checkbox>
              <Checkbox
                checked={!!countedAsCvSent}
                onChange={() => setCountedAsCvSent(!countedAsCvSent)}
                style={{ fontSize: '16px', marginInlineStart: 0 }}
              >
                Counted as CV sent
              </Checkbox>
            </Col>

            <Col span={4}>
              <Select
                style={{ width: '100%' }}
                value={daysToReminder}
                onChange={(value) => setDaysToReminder(value)}
                options={daysToReminderOptions}
              />
            </Col>

            <Col span={5}>
              <Select
                style={{ width: '100%' }}
                value={responsibility}
                onChange={(value: string) => setResponsibility(value)}
                options={reponsibilityOptions}
              />
            </Col>

            <Col span={5}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <StyledButton onClick={handleCancelEditItem}>Cancel</StyledButton>
                <StyledButton type="primary" onClick={handleSaveItem} icon={<SaveButtonIcon />}>
                  Save
                </StyledButton>
              </div>
            </Col>
          </Row>
        </InputListItem>
      ) : (
        <StyledListItem>
          <Row gutter={10} align="middle">
            <Col span={5}>{item.name}</Col>

            <Col span={5}>
              <Checkbox disabled checked={item.actionRequired} style={{ fontSize: '16px' }}>
                Action required
              </Checkbox>
              <Checkbox
                disabled
                checked={item.countedAsCvSent}
                style={{ fontSize: '16px', marginInlineStart: 0, whiteSpace: 'nowrap' }}
              >
                Counted as CV sent
              </Checkbox>
            </Col>

            <Col span={4}>
              {item.daysToReminder
                ? `${item.daysToReminder} day${item.daysToReminder === 1 ? '' : 's'}`
                : 'DO NOT SEND'}
            </Col>

            <Col span={5}>{item.responsibility}</Col>

            <Col span={5}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <StyledEditButton
                  type="text"
                  size="small"
                  onClick={() => setEdit(true)}
                  icon={<Icon name="Edit" inlineStyle={false} />}
                />
                <StyledDeleteButton
                  type="text"
                  size="small"
                  onClick={() => onDelete(item.id)}
                  icon={<Icon inlineStyle={false} name="Delete" />}
                />
              </div>
            </Col>
          </Row>
        </StyledListItem>
      )}
    </>
  );
};

export const RecruitmentStatusList: React.FC = () => {
  const { data: recruitmentStatuses } = useGetRecruitmentStatusDictionary();

  const addRecruitmentStatusDefinition = useAddRecruitmentStatusDefinition();
  const deleteRecruitmentStatusDefinition = useDeleteRecruitmentStatusDefinition();

  const [name, setName] = useState('');
  const [actionRequired, setActionRequired] = useState(false);
  const [countedAsCvSent, setCountedAsCvSent] = useState(false);
  const [responsibility, setResponsibility] = useState('');
  const [daysToReminder, setDaysToReminder] = useState<number | null>(null);

  const handleAddRecruitmentStatus = () => {
    addRecruitmentStatusDefinition.mutate({
      data: {
        name,
        actionRequired,
        countedAsCvSent,
        responsibility,
        daysToReminder,
      },
    });
  };

  const handleDeleteRecruitmentStatus = (id: number) => {
    deleteRecruitmentStatusDefinition.mutate({ queryParams: { id } });
  };

  if (!recruitmentStatuses) return <></>;

  return (
    <>
      <ListLabel>Dictionary items</ListLabel>
      <InputListItem>
        <Row gutter={10} align="middle">
          <Col span={5}>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Col>

          <Col span={5}>
            <Checkbox
              checked={actionRequired}
              onChange={() => setActionRequired(!actionRequired)}
              style={{ fontSize: '16px' }}
            >
              Action required
            </Checkbox>
            <Checkbox
              checked={countedAsCvSent}
              onChange={() => setCountedAsCvSent(!countedAsCvSent)}
              style={{ fontSize: '16px', marginInlineStart: 0 }}
            >
              Counted as CV sent
            </Checkbox>
          </Col>

          <Col span={4}>
            <Select
              style={{ width: '100%' }}
              value={daysToReminder}
              onChange={(value) => setDaysToReminder(value)}
              options={daysToReminderOptions}
            />
          </Col>

          <Col span={5}>
            <Select
              style={{ width: '100%' }}
              value={responsibility}
              onChange={(value: string) => setResponsibility(value)}
              options={reponsibilityOptions}
            />
          </Col>

          <Col span={5}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <StyledButton
                type="primary"
                disabled={!name}
                onClick={handleAddRecruitmentStatus}
                icon={<AddButtonIcon />}
              >
                Add
              </StyledButton>
            </div>
          </Col>
        </Row>
      </InputListItem>

      <DictionaryItemsListContainer>
        {flatten([recruitmentStatuses])
          .filter((item) => item.name.toLowerCase().includes(name.toLowerCase()))
          .map((item) => (
            <RecruitmentStatusListItem
              key={item.id}
              item={item}
              onDelete={handleDeleteRecruitmentStatus}
              onSave={handleAddRecruitmentStatus}
            />
          ))}
      </DictionaryItemsListContainer>
    </>
  );
};
